import { AnimatePresence, motion } from "framer-motion";
import { Check, PencilLine, RotateCcw, SlidersHorizontal } from "lucide-react";
import { Button } from "../ui/Button";

interface Props {
  /** Visibility & content driver. Hide by passing `null`. */
  status: {
    /** True iff `processStroke` (and any union step) succeeded. */
    ok: boolean;
    /** Headline copy (e.g. "Snapped to 90° corners"). */
    message: string;
    /** Whether the stroke is being merged into the existing room. */
    appendMode: boolean;
    /** Whether the stroke is an interior wall. */
    wallMode?: boolean;
  } | null;
  /** "Use this room" / "Add this section" — commits the pending sketch. */
  onConfirm: () => void;
  /** "Redraw" — clears the pending sketch & returns to draw mode. */
  onRedraw: () => void;
  /** "Tweak with handles" — commits then switches to Edit tool. */
  onTweak: () => void;
}

export function SketchConfirmBar({
  status,
  onConfirm,
  onRedraw,
  onTweak,
}: Props) {
  return (
    <AnimatePresence>
      {status && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ type: "spring", damping: 28, stiffness: 320 }}
          className="absolute left-3 right-3 md:left-6 md:right-6 bottom-[var(--float-bottom)] z-20 pointer-events-none"
          role="status"
        >
          <div className="mx-auto max-w-md pointer-events-auto bg-white rounded-2xl ring-1 ring-ink-200 shadow-xl shadow-ink-900/15 overflow-hidden">
            <div className="px-4 py-3 flex items-start gap-2 border-b border-ink-100">
              <div
                className={
                  status.ok
                    ? "size-8 shrink-0 rounded-xl bg-accent-100 text-accent-700 inline-flex items-center justify-center"
                    : "size-8 shrink-0 rounded-xl bg-rose-100 text-rose-700 inline-flex items-center justify-center"
                }
              >
                {status.ok ? (
                  <Check className="size-4" />
                ) : (
                  <PencilLine className="size-4" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-ink-900">
                  {status.ok
                    ? status.wallMode
                      ? "Wall ready"
                      : status.appendMode
                        ? "Addition ready"
                        : "Room ready"
                    : "Try again"}
                </div>
                <div className="text-xs text-ink-500 mt-0.5">
                  {status.message}
                </div>
              </div>
            </div>
            <div className="px-3 py-2 flex items-center gap-2">
              {status.ok ? (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-1"
                    onClick={onConfirm}
                  >
                    <Check className="size-4" />
                    {status.wallMode
                      ? "Add this wall"
                      : status.appendMode
                        ? "Add this section"
                        : "Use this room"}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onTweak}
                    title="Apply & switch to handle editing"
                  >
                    <SlidersHorizontal className="size-4" />
                    <span className="max-[359px]:sr-only">Tweak</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRedraw}
                    title="Discard and draw again"
                  >
                    <RotateCcw className="size-4" />
                    <span className="max-[419px]:sr-only">Redraw</span>
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  className="flex-1"
                  onClick={onRedraw}
                >
                  <RotateCcw className="size-4" />
                  Try again
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
